import { Injectable } from '@angular/core';
import { BrowserInfo, LocaleInfo, ScreenInfo } from '../diagnostics.models';

@Injectable({
  providedIn: 'root',
})
export class BrowserInfoService {
  public getBrowserInfo(): BrowserInfo {
    const userAgent = navigator.userAgent;
    const browserData = this._parseBrowserInfo(userAgent);

    return {
      name: browserData.name,
      version: browserData.version,
      userAgent: userAgent,
      language: navigator.language,
      languages: [...navigator.languages],
      cookiesEnabled: navigator.cookieEnabled,
      localStorageAvailable: this._checkLocalStorage(),
      sessionStorageAvailable: this._checkSessionStorage(),
    };
  }

  public getScreenInfo(): ScreenInfo {
    return {
      resolution: `${screen.width}x${screen.height}`,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      devicePixelRatio: window.devicePixelRatio || 1,
      touchSupported: this._checkTouchSupport(),
    };
  }

  public getLocaleInfo(): LocaleInfo {
    const date = new Date();
    return {
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      timezoneOffset: date.getTimezoneOffset(),
      locale: navigator.language,
    };
  }

  private _parseBrowserInfo(userAgent: string): {
    name: string;
    version: string;
  } {
    const browsers: Array<{ name: string; regex: RegExp }> = [
      { name: 'Edge', regex: /Edg(?:e|A|iOS)?\/(\d+(?:\.\d+)*)/ },
      { name: 'Chrome', regex: /Chrome\/(\d+(?:\.\d+)*)/ },
      { name: 'Firefox', regex: /Firefox\/(\d+(?:\.\d+)*)/ },
      { name: 'Safari', regex: /Version\/(\d+(?:\.\d+)*).*Safari/ },
      { name: 'Opera', regex: /OPR\/(\d+(?:\.\d+)*)/ },
      { name: 'Internet Explorer', regex: /(?:MSIE |rv:)(\d+(?:\.\d+)*)/ },
    ];

    for (const browser of browsers) {
      const match = userAgent.match(browser.regex);
      if (match) {
        return {
          name: browser.name,
          version: match[1],
        };
      }
    }

    return {
      name: 'Unknown',
      version: 'Unknown',
    };
  }

  private _checkLocalStorage(): boolean {
    try {
      const testKey = '__storage_test__';
      localStorage.setItem(testKey, testKey);
      localStorage.removeItem(testKey);
      return true;
    } catch {
      return false;
    }
  }

  private _checkSessionStorage(): boolean {
    try {
      const testKey = '__storage_test__';
      sessionStorage.setItem(testKey, testKey);
      sessionStorage.removeItem(testKey);
      return true;
    } catch {
      return false;
    }
  }

  private _checkTouchSupport(): boolean {
    return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  }
}
